import dayjs from 'dayjs';

import { resolveColor } from 'constants/colors';
import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { DEFAULT_MILESTONE_COLOR_NAME } from 'constants/milestones';
import { MAX_PERCENTAGE_VALUE } from 'constants/number';
import { formatDate } from 'utils/date';
import { getMilestoneCompletedDate } from 'utils/milestones';

import { MILESTONE_ROADMAP_STATUS, ROADMAP_IN_PROGRESS_TEXT } from './MilestoneRoadmap.constants';

export const getInProgressLabel = daysOnCurrent =>
  daysOnCurrent && daysOnCurrent > 0
    ? `${ROADMAP_IN_PROGRESS_TEXT} · day ${daysOnCurrent}`
    : ROADMAP_IN_PROGRESS_TEXT;

// Each milestone exposes a palette name; resolve it to the same system color
// the chip uses: dot = vivid fill, bg = soft tint, fg = deep readable text.
const getMilestoneColors = color => {
  const resolved = resolveColor(color) ?? resolveColor(DEFAULT_MILESTONE_COLOR_NAME);

  return {
    strong: resolved.dot,
    soft: resolved.bg,
    deep: resolved.fg,
  };
};

const getMilestoneRoadmapStatus = (order, lastCompletedOrder) => {
  switch (true) {
    case order < lastCompletedOrder:
      return MILESTONE_ROADMAP_STATUS.COMPLETED;

    case order === lastCompletedOrder:
      return MILESTONE_ROADMAP_STATUS.CURRENT;

    default:
      return MILESTONE_ROADMAP_STATUS.UPCOMING;
  }
};

const getWholeDayDiff = (fromValue, toValue) => {
  const from = dayjs(fromValue);
  const to = dayjs(toValue);

  if (!from.isValid() || !to.isValid()) {
    return null;
  }

  return Math.max(to.startOf('day').diff(from.startOf('day'), 'day'), 0);
};

const getDurationLabel = days => {
  if (days === null) {
    return null;
  }

  return days === 1 ? '1 day' : `${days} days`;
};

export const getReachedCount = (lastCompleted, total) => {
  const lastCompletedOrder = lastCompleted?.order ?? -1;

  return Math.min(lastCompletedOrder + 1, total);
};

export const getRoadmapPercentage = (reachedCount, total) =>
  total ? Math.round((reachedCount / total) * MAX_PERCENTAGE_VALUE) : 0;

// Earliest reached milestone date → today, in whole days.
export const getDaysInProcess = milestones => {
  const startDate = milestones?.map(getMilestoneCompletedDate).filter(Boolean).sort()[0];

  return startDate ? getWholeDayDiff(startDate, dayjs()) : null;
};

export const buildMilestoneRoadmapRows = ({ milestones, lastCompleted }) => {
  if (!milestones?.length) {
    return [];
  }

  const lastCompletedOrder = lastCompleted?.order ?? -1;

  return milestones.map((milestone, index) => {
    const { order, name, color, definitionId } = milestone;
    const status = getMilestoneRoadmapStatus(order, lastCompletedOrder);
    const completedDate = getMilestoneCompletedDate(milestone);
    const isCurrent = status === MILESTONE_ROADMAP_STATUS.CURRENT;
    const isUpcoming = status === MILESTONE_ROADMAP_STATUS.UPCOMING;

    const nextCompletedDate = getMilestoneCompletedDate(milestones[index + 1]);
    const stageDays =
      status === MILESTONE_ROADMAP_STATUS.COMPLETED && nextCompletedDate
        ? getWholeDayDiff(completedDate, nextCompletedDate)
        : null;
    const daysOnCurrent =
      isCurrent && completedDate ? getWholeDayDiff(completedDate, dayjs()) : null;

    return {
      key: `${definitionId ?? order ?? 'milestone'}-${index}`,
      definitionId,
      name,
      status,
      isCurrent,
      colors: getMilestoneColors(color),
      date:
        isUpcoming || !completedDate
          ? null
          : formatDate(completedDate, SHORT_MONTH_DAY_YEAR_FORMAT),
      durationLabel: getDurationLabel(stageDays),
      daysOnCurrent,
    };
  });
};

export const getCurrentMilestone = rows => rows.find(row => row.isCurrent) ?? null;

export const getNextMilestone = rows => {
  const currentIndex = rows.findIndex(row => row.isCurrent);

  return currentIndex === -1 ? (rows[0] ?? null) : (rows[currentIndex + 1] ?? null);
};
